import type { PhotoAsset } from '../../types';
import { el, ensureStyle } from '../dom';
import { imageUrl } from '../../core/media/image';
import { sourceLabel } from '../../core/attributions';
import { t } from '../../strings';

// A photo frame that returns synchronously with a paper-coloured skeleton and
// fills itself in once the pack fetcher resolves. Photos only exist after the
// user downloads the region pack, so "not available" is a normal state, not an
// error — it gets an honest label rather than a broken-image icon.
//
// The frame is a wrapper rather than a bare <img> because pseudo-elements don't
// render reliably on replaced elements; the label is a real child. Callers size
// the frame (aspect-ratio for a hero, fixed px for a list thumb), and the photo
// is fitted inside whatever shape they chose — see the object-fit note below.

ensureStyle(
  'species-photo-css',
  `
  .spphoto { position:relative; overflow:hidden; background:var(--sunk); }
  /* CONTAIN, never cover. A frame has a shape of its own — a 4:3 hero, a square
     grid cell, a 54px list thumb — and the photograph inside it does not, so
     filling the frame means cutting something off the photograph. What gets cut
     is whatever is nearest an edge, which for a bird photographed head-high is
     the top of its head. An identification app cannot show a bird with its
     crown missing and then ask what it is. The letterbox is the frame's own
     background, so it reads as mount board rather than as a gap. */
  .spphoto img { display:block; width:100%; height:100%; object-fit:contain; }
  /* A quiz's occasional left-right flip; see core/media/mirror.ts. On the img,
     not the frame, so a label laid over the frame still reads forwards. */
  .spphoto.mirror img { transform:scaleX(-1); }
  .spphoto .nophoto { display:none; }
  .spphoto.missing .nophoto { display:flex; position:absolute; inset:0; align-items:center;
    justify-content:center; text-align:center; padding:6px; color:var(--ink-soft);
    font-size:9px; letter-spacing:.06em; line-height:1.4; }
`,
);

export interface SpeciesPhotoOptions {
  /** Load the 160px thumbnail instead of the card-sized image. */
  thumb?: boolean;
  alt?: string;
  /**
   * Hold the fetch until the frame is near the viewport.
   *
   * Every photo is a Cache Storage read plus a blob decode, so a list that asks
   * for all of them at once pays for the whole region to show ten rows — at 120
   * species the Library was doing 120 of them before the first scroll. Frames
   * hidden by a filter never intersect, so this also makes filtering free.
   */
  lazy?: boolean;
  /**
   * Show the photograph flipped left to right — a quiz's way of keeping a
   * learner from memorising the picture instead of the bird. Pair it with
   * `photoCredit(photo, true)`, which says so.
   */
  mirror?: boolean;
}

// One observer for every lazy frame in the app. A frame drops out of it as soon
// as it has asked for its image, so this never grows past what is on screen.
const wanted = new Map<Element, () => void>();
let watcher: IntersectionObserver | null = null;

function whenNearViewport(node: Element, run: () => void): void {
  // No IntersectionObserver (jsdom, ancient Safari): load eagerly. Slower, but
  // a blank list would be a bug, and a slow list is only the old behaviour.
  if (typeof IntersectionObserver === 'undefined') return run();
  if (!watcher) {
    watcher = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const fn = wanted.get(e.target);
          wanted.delete(e.target);
          watcher!.unobserve(e.target);
          fn?.();
        }
      },
      // A screen of lead time, so scrolling meets loaded thumbnails.
      { rootMargin: '400px 0px' },
    );
  }
  wanted.set(node, run);
  watcher.observe(node);
}

/** Drop a frame's pending request — it has been given a different photo. */
function cancelWatch(node: Element): void {
  if (wanted.delete(node)) watcher?.unobserve(node);
}

export interface PhotoFrame {
  element: HTMLElement;
  /** Swap in a different photo (the detail page's thumb strip). */
  show: (photo: PhotoAsset | undefined) => void;
}

export function speciesPhoto(
  photo: PhotoAsset | undefined,
  opts: SpeciesPhotoOptions = {},
): PhotoFrame {
  const img = el('img', { alt: opts.alt ?? '' }) as HTMLImageElement;
  const note = el('span.nophoto', {});
  const frame = el('div.spphoto', {}, img, note);
  if (opts.mirror) frame.classList.add('mirror');

  // The photograph's own shape, published to CSS the moment it is known.
  //
  // Frames that show ONE picture — the quiz's prompt, the species page's hero —
  // set `aspect-ratio: var(--photo-ratio, 4/3)` and so end up exactly the shape
  // of the picture in them, with the 4:3 holding the space until it loads.
  // Since nothing is cropped any more, a fixed frame would otherwise mean a
  // band of empty linen over and under every portrait shot. Frames whose shape
  // is the point — the square grid, the round-cornered list thumbs — simply
  // don't read the property, and letterbox inside their own outline as before.
  img.addEventListener('load', () => {
    if (!img.naturalWidth || !img.naturalHeight) return;
    frame.style.setProperty('--photo-ratio', `${img.naturalWidth} / ${img.naturalHeight}`);
  });

  // Only the newest request may paint, so a fast thumb tap can't be overwritten
  // by a slower earlier one resolving late.
  let token = 0;

  function show(next: PhotoAsset | undefined): void {
    const mine = ++token;
    cancelWatch(frame);
    frame.classList.remove('missing');
    // A frame being given a different photo is a frame whose shape is no longer
    // known: drop back to the placeholder ratio until the new one loads, or the
    // hero would hold a portrait's height around a landscape shot.
    frame.style.removeProperty('--photo-ratio');
    if (!next) {
      img.removeAttribute('src');
      note.textContent = t('species.photo.none');
      frame.classList.add('missing');
      return;
    }
    note.textContent = t('species.photo.notDownloaded');
    const fetchIt = () => {
      // No `isConnected` guard: the frame is built before it is appended, so the
      // fetch can legitimately resolve while it is still detached. A stale write
      // to a torn-down element is harmless; a dropped one leaves a blank photo.
      void imageUrl(opts.thumb ? next.thumb : next.file).then((url) => {
        if (mine !== token) return;
        if (url) img.src = url;
        else frame.classList.add('missing');
      });
    };
    // The token check inside also covers the deferred case: a frame swapped to
    // a different photo before it ever scrolled into view loads only the last.
    if (opts.lazy) whenNearViewport(frame, fetchIt);
    else fetchIt();
  }

  show(photo);
  return { element: frame, show };
}

/**
 * `author · CC-BY-NC · iNaturalist` — the credit every displayed photo needs.
 *
 * `mirrored` adds a note that the picture on screen is flipped. The BY licences
 * ask for changes to be indicated, and a photographer whose bird is facing the
 * wrong way deserves to be told it was not their doing.
 */
export function photoCredit(photo: PhotoAsset, mirrored = false): string {
  return t(mirrored ? 'species.creditMirrored' : 'species.credit', {
    author: photo.author,
    license: photo.license,
    source: sourceLabel(photo.source),
  });
}
